import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../helpers/axios';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

const EMPTY_STATS = { ptpToPaidPct: 0, brokenPct: 0, activePtpCount: 0, avgDays: 0 };

export default function ManagerPtp({ initialData }) {
  const [stats, setStats]       = useState(initialData?.stats    || EMPTY_STATS);
  const [ptpLoans, setPtpLoans] = useState(initialData?.loans    || []);
  const [loading, setLoading]   = useState(!initialData);

  useEffect(() => {
    if (initialData) return;
    axiosInstance
      .get('/collection-incentive/manager/ptp-tracker')
      .then((res) => {
        const d = res.data.data;
        setStats(d.stats || EMPTY_STATS);
        setPtpLoans(d.loans || []);
      })
      .catch((error) => {
        alert(error?.response?.data?.message || 'Failed to load PTP tracker');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!initialData) return;
    setStats(initialData.stats || EMPTY_STATS);
    setPtpLoans(initialData.loans || []);
    setLoading(false);
  }, [initialData]);

  if (loading) {
    return <div style={{ padding: '20px', color: '#667085' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '10px' }}>

      {/* ── Stat Cards Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>

        {[
          {
            label: 'PTP → PAID',
            value: `${stats.ptpToPaidPct}%`,
            icon: <MonetizationOnOutlinedIcon style={{ fontSize: '24px', color: '#16a34a' }} />,
            iconBg: '#ecfdf5',
            cardBg: 'linear-gradient(to bottom left, #86efac 0%, #ffffff 60%)',
          },
          {
            label: 'Broken',
            value: `${stats.brokenPct}%`,
            icon: <GppBadOutlinedIcon style={{ fontSize: '24px', color: '#f97316' }} />,
            iconBg: '#fff7ed',
            cardBg: 'linear-gradient(to bottom left, #fdd888 0%, #ffffff 60%)',
          },
          {
            label: 'Active',
            value: stats.activePtpCount,
            icon: <AssignmentTurnedInOutlinedIcon style={{ fontSize: '24px', color: '#2563eb' }} />,
            iconBg: '#eff6ff',
            cardBg: 'linear-gradient(to bottom left, #93c5fd 0%, #ffffff 60%)',
          },
          {
            label: 'Avg Days',
            value: stats.avgDays,
            icon: <TimerOutlinedIcon style={{ fontSize: '24px', color: '#7c3aed' }} />,
            iconBg: '#f5f3ff',
            cardBg: 'linear-gradient(to bottom left, #d8b4fe 0%, #ffffff 60%)',
          },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: card.cardBg,
              borderRadius: '12px',
              padding: '14px 16px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: '11px', color: '#667085', fontWeight: '600', fontFamily: 'Inter-Medium' }}>
                {card.label}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '22px', fontWeight: '700', color: '#101828', lineHeight: 1.2, fontFamily: 'Inter-Medium' }}>
                {card.value}
              </p>
            </div>
            <div style={{
              width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0,
              backgroundColor: card.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {card.icon}
            </div>
          </div>
        ))}

      </div>

      {/* ── Active PTPs Table ── */}
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
          <p style={{ margin: 0, fontFamily: 'Inter-Medium', fontSize: '14px', fontWeight: '600', color: '#344054' }}>
            🤝 Active PTPs
          </p>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Inter-Medium', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafbfc' }}>
                {['Loan ID', 'Bucket', 'Agent', 'PTP Date'].map((columnHeader) => (
                  <th
                    key={columnHeader}
                    style={{
                      padding: '4px 8px',
                      textAlign: 'left',
                      color: '#667085',
                      fontWeight: '600',
                      fontSize: '11px',
                      borderBottom: '1px solid #eee',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {columnHeader}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ptpLoans.length > 0 ? (
                ptpLoans.map((loan, index) => (
                  <tr key={loan.loanId || index} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '4px 8px', fontWeight: '500', color: '#344054' }}>
                      {loan.loanId || '—'}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                        }}
                      >
                        {loan.bucket || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {loan.agentName || loan.agentId || '—'}
                    </td>
                    <td style={{ padding: '4px 8px', color: '#344054' }}>
                      {loan.ptpDate
                        ? new Date(loan.ptpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                        : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#667085' }}>
                    No active PTP loans found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
